import React, { useEffect, useState, useCallback } from "react";
import Select from "react-select";
import axios from "axios";
import BlockUi from "react-block-ui";
import { PLAN_TYPES } from "./constants";
import { JdsButton } from "../../../components/jds/JdsButton";
import { jdsSelectProps } from "../../../components/jds/selectProps";
import "../../../components/jds/jds.css";
import "../addPlanGroups.css";

// Rótulo acima do campo; obrigatório leva asterisco vermelho depois do texto.
const Label = ({ required, children, ...rest }) => (
  <label {...rest}>
    {children}
    {required && (
      <span className="text-danger" aria-hidden="true">
        {" "}*
      </span>
    )}
  </label>
);

// Erro abaixo do campo.
const FieldError = ({ show, children }) =>
  show ? <div className="invalid-feedback d-block">{children}</div> : null;

export default function Step1(props) {
  const [featureLimits, setFeatureLimits] = useState(
    props.formik.values.limits
  );
  const [isLoading, setIsLoading] = useState(true);
  const [universalCredits, setUniversalCredits] = useState(0);

  const { values, errors, touched, handleChange, setFieldValue } = props.formik;
  const loadFeatures = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/features/?universal=${values.is_universal}`,
      {
        params: { plan_type: values.plan_type }
      }
    );

    let allFeatures = response.data.data;
    setUniversalCredits(response.data.universalCreditValue || 0);
    const featureLimitsMap = {};
    if (props.id) {
      props.formik.values.limits.forEach(limit => {
        featureLimitsMap[limit.feature_id] = {
          limit: limit.limit,
          trial_limit: limit.trial_limit
        };
      });
    }
    const featuresWithLimits = allFeatures.map(feature => {
      const limits = featureLimitsMap[feature.id] || {
        limit: 0,
        trial_limit: 0
      };


      const isUniversalFeature = feature.name === "universal_credits";
      const isUniversalMode = values?.is_universal;
      const hasNoIdParams = !props.id;

      const featureIsUniversalWithoutIdParams = isUniversalFeature && isUniversalMode && hasNoIdParams;

      return {
        feature_id: feature.id,
        feature_name: feature.description,
        limit:
            featureIsUniversalWithoutIdParams ? universalCredits : limits.limit,
        trial_limit: limits.trial_limit
      };
    });

    setFeatureLimits(featuresWithLimits);
    setFieldValue("limits", featuresWithLimits);
    setIsLoading(false);
  };

  const handleSwitchChange = useCallback(
    event => {
      const isChecked = event.target.checked;

      props.formik.setFieldValue("is_universal", isChecked);
    },
    [props.formik]
  );

  useEffect(() => {
    loadFeatures();
  }, []);

  useEffect(() => {
    loadFeatures();
  }, [values.is_universal, values.plan_type, universalCredits]);

  const RenderFeatures = useCallback(
    ({ limits }) => (
      <div className="apg-features">
        <div />
        <Label className="apg-features-head" required>
          Trial
        </Label>
        <Label className="apg-features-head" required>
          Limite
        </Label>
        {!isLoading &&
          limits.map((feature, index) => (
            <React.Fragment key={feature.feature_id}>
              <label
                htmlFor={`limits_${index}_trial_limit`}
                className="apg-feature-name"
              >
                {feature.feature_name}
              </label>
              <input
                id={`limits_${index}_trial_limit`}
                type="number"
                aria-label={`Trial de ${feature.feature_name}`}
                name={`limits[${index}].trial_limit`}
                value={feature.trial_limit}
                onChange={handleChange}
                className={
                  "form-control " +
                  (errors.name && touched.name ? "is-invalid" : "")
                }
              />
              <input
                id={`limits_${index}_limit`}
                type="number"
                aria-label={`Limite de ${feature.feature_name}`}
                name={`limits[${index}].limit`}
                value={feature.limit}
                onChange={handleChange}
                className={
                  "form-control " +
                  (errors.name && touched.name ? "is-invalid" : "")
                }
              />
            </React.Fragment>
          ))}
      </div>
    ),
    [handleChange, errors, touched, isLoading]
  );

  const planTypeInvalid =
    props.formik.errors.plan_type && props.formik.touched.plan_type;
  const nameInvalid = props.formik.errors.name && props.formik.touched.name;

  return (
    <BlockUi tag="div" blocking={props.formik.isSubmitting}>
      <div className="jds-card">
        <section className="apg-section">
          <h2 className="jds-title-sm apg-section-title">Dados do grupo</h2>
          <div className="apg-grid">
            <div className="form-group">
              <Label htmlFor="name" required>
                Nome
              </Label>
              <input
                id="name"
                type="text"
                name="name"
                className={"form-control " + (nameInvalid ? "is-invalid" : "")}
                value={props.formik.values.name}
                onChange={props.formik.handleChange}
              />
              <FieldError show={nameInvalid}>{props.formik.errors.name}</FieldError>
            </div>
            <div className="form-group">
              <Label htmlFor="plan_type" required>
                Tipo de planos
              </Label>
              <Select
                {...jdsSelectProps}
                inputId="plan_type"
                name="plan_type"
                options={PLAN_TYPES}
                className={"apg-select" + (planTypeInvalid ? " is-invalid" : "")}
                onChange={option =>
                  props.formik.setFieldValue("plan_type", option.value)
                }
                value={PLAN_TYPES.find(
                  option => option.value === props.formik.values.plan_type
                )}
                placeholder="Selecione"
                getOptionLabel={option => option.label}
                getOptionValue={option => option.value}
                noOptionsMessage={() => <span>Sem resultados.</span>}
              />
              <FieldError show={planTypeInvalid}>
                {props.formik.errors.plan_type}
              </FieldError>
            </div>
            <div className="apg-switch-row">
              <span className="switch switch-primary mr-1">
                <label>
                  <input
                    type="checkbox"
                    id="is_universal"
                    name="is_universal"
                    checked={Boolean(values.is_universal)}
                    onChange={handleSwitchChange}
                  />
                  <span></span>
                </label>
              </span>
              <label htmlFor="is_universal" className="apg-switch-label">
                Plano universal
              </label>
            </div>
          </div>
        </section>

        <section className="apg-section">
          <h2 className="jds-title-sm apg-section-title">
            Produtos (features)
          </h2>
          <RenderFeatures limits={values.limits} />
        </section>

        <div className="apg-actions">
          <JdsButton onClick={props.formik.handleSubmit}>
            Salvar grupo de planos
          </JdsButton>
        </div>
      </div>
    </BlockUi>
  );
}
